import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import AdminForm from '../../components/admin/AdminForm';
import { athletesAPI } from '../../services/api';

const AdminAthletes = () => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState<any>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'view' | 'edit' | 'add'>('view');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchAthletes();
  }, [navigate]);

  const fetchAthletes = async () => {
    try {
      setLoading(true);
      const response = await athletesAPI.getAll();
      if (response.records) {
        setAthletes(response.records);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
      // Use mock data in case of error
      setAthletes([
        {
          id: 1,
          name: 'Cherif Fall',
          country: 'Sénégal',
          category: 'professional',
          age: 24,
          specialty: 'Shortboard',
          ranking: 1,
          image_url: 'https://images.pexels.com/photos/1308881/pexels-photo-1308881.jpeg?auto=compress&cs=tinysrgb&w=600',
          bio_fr: 'Prodige du surf sénégalais, Cherif Fall incarne la nouvelle génération de surfeurs africains.',
          bio_en: 'Senegalese surf prodigy, Cherif Fall embodies the new generation of African surfers.',
          achievements: 'Champion Africa Surf Tour 2024,Qualification WSL Challenger Series,3x Champion Sénégal',
          sponsors: 'Quiksilver,Red Bull,FCS',
          social_instagram: '@cheriffall_surf',
          social_youtube: 'Cherif Fall Surf',
          stats_competitions: 28,
          stats_wins: 12,
          stats_podiums: 20
        },
        {
          id: 2,
          name: 'Aminata Diop',
          country: 'Sénégal',
          category: 'professional',
          age: 22,
          specialty: 'Shortboard',
          ranking: 1,
          image_url: 'https://images.pexels.com/photos/1772123/pexels-photo-1772123.jpeg?auto=compress&cs=tinysrgb&w=600',
          bio_fr: 'Pionnière du surf féminin au Sénégal, Aminata inspire toute une génération de jeunes filles.',
          bio_en: 'Pioneer of women\'s surfing in Senegal, Aminata inspires a whole generation of young girls.',
          achievements: 'Championne Africa Surf Tour 2024,Ambassadrice Jeunesse ASCsurf,Record national féminin',
          sponsors: 'Roxy,Billabong,Futures',
          social_instagram: '@aminata_surf',
          social_youtube: 'Aminata Diop',
          stats_competitions: 22,
          stats_wins: 8,
          stats_podiums: 15
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAthlete(null);
    setViewMode('add');
    setShowModal(true);
  };

  const handleEdit = (athlete: any) => {
    setEditingAthlete(athlete);
    setViewMode('edit');
    setShowModal(true);
  };

  const handleView = (athlete: any) => {
    setEditingAthlete(athlete);
    setViewMode('view');
    setShowModal(true);
  };

  const handleDelete = async (athlete: any) => {
    try {
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'athlète ${athlete.name} ?`)) {
        await athletesAPI.delete(athlete.id);
        setAthletes(prev => prev.filter(item => item.id !== athlete.id));
      }
    } catch (error) {
      console.error('Error deleting athlete:', error);
      alert('Erreur lors de la suppression de l\'athlète');
      
      // Simulate successful delete for demo purposes
      setAthletes(prev => prev.filter(item => item.id !== athlete.id));
    }
  };

  const handleSubmit = async (formData: any) => {
    try {
      setFormLoading(true);
      
      if (editingAthlete) {
        // Update existing athlete
        const updatedData = { ...formData, id: editingAthlete.id };
        await athletesAPI.update(updatedData);
        
        setAthletes(prev => prev.map(item => 
          item.id === editingAthlete.id ? { ...item, ...formData } : item
        ));
      } else {
        // Add new athlete
        const response = await athletesAPI.create(formData);
        
        if (response.id) {
          const newAthlete = {
            id: response.id,
            ...formData,
            created_at: new Date().toISOString()
          };
          setAthletes(prev => [newAthlete, ...prev]);
        }
      }
      
      setShowModal(false);
      setEditingAthlete(null);
    } catch (error) {
      console.error('Error saving athlete:', error);
      alert('Erreur lors de l\'enregistrement de l\'athlète');
      
      // Simulate successful save for demo purposes
      if (editingAthlete) {
        setAthletes(prev => prev.map(item => 
          item.id === editingAthlete.id ? { ...item, ...formData } : item
        ));
      } else {
        const newId = Math.max(0, ...athletes.map(item => item.id)) + 1;
        const newAthlete = {
          id: newId,
          ...formData,
          created_at: new Date().toISOString()
        };
        setAthletes(prev => [newAthlete, ...prev]);
      }
      
      setShowModal(false);
      setEditingAthlete(null);
    } finally {
      setFormLoading(false);
    }
  };

  const formFields = [
    {
      name: 'name',
      label: 'Nom',
      type: 'text' as const,
      required: true,
      placeholder: 'Nom complet de l\'athlète'
    },
    {
      name: 'country',
      label: 'Pays',
      type: 'text' as const,
      required: true,
      placeholder: 'Pays d\'origine'
    },
    {
      name: 'category',
      label: 'Catégorie',
      type: 'select' as const,
      required: true,
      options: [
        { value: 'professional', label: 'Professionnel' },
        { value: 'emerging', label: 'Talent émergent' }
      ]
    },
    {
      name: 'age',
      label: 'Âge',
      type: 'text' as const,
      required: true,
      placeholder: '25'
    },
    {
      name: 'specialty',
      label: 'Spécialité',
      type: 'text' as const,
      required: true,
      placeholder: 'Shortboard, Longboard, Big Wave...'
    },
    {
      name: 'ranking',
      label: 'Classement',
      type: 'text' as const,
      required: true,
      placeholder: '1'
    },
    {
      name: 'image_url',
      label: 'URL de l\'image',
      type: 'text' as const,
      placeholder: 'https://example.com/image.jpg'
    },
    {
      name: 'bio_fr',
      label: 'Biographie (Français)',
      type: 'textarea' as const,
      placeholder: 'Biographie de l\'athlète en français',
      rows: 4
    },
    {
      name: 'bio_en',
      label: 'Biographie (Anglais)',
      type: 'textarea' as const,
      placeholder: 'Biographie de l\'athlète en anglais',
      rows: 4
    },
    {
      name: 'achievements',
      label: 'Réalisations',
      type: 'textarea' as const,
      placeholder: 'Réalisation1,Réalisation2,Réalisation3',
      rows: 3
    },
    {
      name: 'sponsors',
      label: 'Sponsors',
      type: 'text' as const,
      placeholder: 'Sponsor1,Sponsor2,Sponsor3'
    },
    {
      name: 'social_instagram',
      label: 'Instagram',
      type: 'text' as const,
      placeholder: '@username'
    },
    {
      name: 'social_youtube',
      label: 'YouTube',
      type: 'text' as const,
      placeholder: 'Nom de la chaîne'
    },
    {
      name: 'stats_competitions',
      label: 'Nombre de compétitions',
      type: 'text' as const,
      placeholder: '25'
    },
    {
      name: 'stats_wins',
      label: 'Nombre de victoires',
      type: 'text' as const,
      placeholder: '10'
    },
    {
      name: 'stats_podiums',
      label: 'Nombre de podiums',
      type: 'text' as const,
      placeholder: '18'
    }
  ];

  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value: string, row: any) => (
        <div className="flex items-center">
          {row.image_url && (
            <img 
              src={row.image_url} 
              alt={value}
              className="w-10 h-10 rounded-full object-cover mr-3"
            />
          )}
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.country}</div>
          </div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Catégorie',
      render: (value: string) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'professional' ? 'bg-gold text-yellow-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {value === 'professional' ? 'PRO' : 'TALENT'}
        </span>
      )
    },
    {
      key: 'age',
      label: 'Âge',
      sortable: true
    },
    {
      key: 'specialty',
      label: 'Spécialité',
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value}</span>
      )
    },
    {
      key: 'ranking',
      label: 'Classement',
      sortable: true,
      render: (value: number) => `#${value}`
    },
    {
      key: 'stats_competitions',
      label: 'Compétitions',
      sortable: true
    },
    {
      key: 'stats_wins',
      label: 'Victoires',
      sortable: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des athlètes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <AdminTable
          title="Gestion des Athlètes"
          data={athletes}
          columns={columns}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable
          filterable
          selectable
          pagination
          itemsPerPage={10}
        />

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingAthlete(null);
          }}
          title={
            viewMode === 'add' 
              ? 'Nouvel athlète' 
              : viewMode === 'edit' 
                ? 'Modifier l\'athlète' 
                : 'Détails de l\'athlète'
          }
          size="lg"
        >
          {viewMode === 'view' ? (
            <div className="space-y-6">
              <div className="flex items-center space-x-6 mb-6">
                {editingAthlete?.image_url && (
                  <img 
                    src={editingAthlete.image_url} 
                    alt={editingAthlete.name} 
                    className="w-32 h-32 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{editingAthlete?.name}</h3>
                  <div className="flex items-center space-x-3 mt-2">
                    <span className="text-gray-600">{editingAthlete?.country}</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      editingAthlete?.category === 'professional' ? 'bg-gold text-yellow-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {editingAthlete?.category === 'professional' ? 'PRO' : 'TALENT'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Âge</div>
                  <div className="font-semibold text-gray-900">{editingAthlete?.age} ans</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Spécialité</div>
                  <div className="font-semibold text-gray-900">{editingAthlete?.specialty}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-500">Classement</div>
                  <div className="font-semibold text-gray-900">#{editingAthlete?.ranking}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{editingAthlete?.stats_competitions}</div>
                  <div className="text-sm text-gray-600">Compétitions</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{editingAthlete?.stats_wins}</div>
                  <div className="text-sm text-gray-600">Victoires</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-orange-600">{editingAthlete?.stats_podiums}</div>
                  <div className="text-sm text-gray-600">Podiums</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Biographie (Français)</h3>
                <p className="text-gray-800 bg-gray-50 p-4 rounded-lg">{editingAthlete?.bio_fr}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Biographie (Anglais)</h3>
                <p className="text-gray-800 bg-gray-50 p-4 rounded-lg">{editingAthlete?.bio_en}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Réalisations</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {editingAthlete?.achievements.split(',').map((achievement: string, index: number) => (
                    <li key={index} className="text-gray-800">{achievement.trim()}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sponsors</h3>
                <div className="flex flex-wrap gap-2">
                  {editingAthlete?.sponsors.split(',').map((sponsor: string, index: number) => (
                    <span key={index} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                      {sponsor.trim()}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Instagram</h3>
                  <p className="text-gray-800">{editingAthlete?.social_instagram}</p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">YouTube</h3>
                  <p className="text-gray-800">{editingAthlete?.social_youtube}</p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setViewMode('edit');
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingAthlete(null);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <AdminForm
              fields={formFields}
              initialData={editingAthlete || {}}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowModal(false);
                setEditingAthlete(null);
              }}
              loading={formLoading}
            />
          )}
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminAthletes;